import _ from 'lodash';
import React from 'react';
import {
  useGetAccountRegistered, usePostRegister, useGetConfirmToken,
} from '../../../services/auth-service';
import { usePostCmSubscribe } from '../../../services/campaign-mailist-service';
import { parseRegistrationErrors, isEmailTaken } from '../../../services/registration-errors';
import { changeBrazeUserIfEnabled } from '../../../services/braze/braze-user-identity';

const buildRegParam = values => _(values)
  .pick('email', 'fullName', 'firstName', 'lastName', 'password')
  .mapKeys((v, k) => _.snakeCase(k))
  .value();

// eslint-disable-next-line react-hooks/exhaustive-deps
const useNullableConstCallback = func => React.useCallback(() => func && func(), []);

export default function useRegister({
  doSubscription, data, onEmailNotUnique, onRegistrationReturned, onComplete,
}) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [isEmailUnique, setIsEmailUnique] = React.useState(true);
  const [oauthProvider, setOauthProvider] = React.useState(null);
  const registrationDataRef = React.useRef(null);
  const [{ data: accountData, loading: eLoading }, accountQuery] = useGetAccountRegistered();
  const [{ data: regData, loading: rLoading, error: regError }, register] = usePostRegister();
  const [{ response: confirmResponse, loading: cLoading }, doConfirm] = useGetConfirmToken();
  const [{ response: subResponse, loading: sLoading }, subscribe] = usePostCmSubscribe();

  const onENU = useNullableConstCallback(onEmailNotUnique);
  const onRR = useNullableConstCallback(onRegistrationReturned);
  const onC = useNullableConstCallback(onComplete);

  React.useEffect(() => {
    if (_.get(data, 'email.length')) {
      setIsEmailUnique(true);
      setOauthProvider(null);
    }
  }, [data]);

  React.useEffect(() => {
    setIsLoading(eLoading || rLoading || cLoading || (doSubscription ? sLoading : false));
  }, [eLoading, rLoading, cLoading, doSubscription, sLoading]);

  React.useEffect(() => {
    const accountExists = _.get(accountData, 'account_exist');
    const provider = _.get(accountData, 'provider');
    if (accountExists === false) {
      const payload = registrationDataRef.current ? buildRegParam(registrationDataRef.current) : {};
      register(payload);
    } else if (accountExists) {
      onENU && onENU();
      setIsEmailUnique(false);
      setOauthProvider(provider || null);
    }
  }, [accountData, onENU, register]);

  React.useEffect(() => {
    if (!rLoading && regError && _.get(regError, 'response.status') === 412) {
      const responseData = _.get(regError, 'response.data');
      const parsed = parseRegistrationErrors(responseData);
      if (isEmailTaken(parsed)) {
        onENU && onENU();
        setIsEmailUnique(false);
      }
    }
  }, [rLoading, regError, onENU]);

  React.useEffect(() => {
    const token = _.get(regData, 'confirmation_token');
    if (token) {
      const brazeExternalId = _.get(regData, 'braze_external_id');
      if (brazeExternalId) {
        changeBrazeUserIfEnabled(brazeExternalId);
      }
      onRR && onRR();
      doConfirm(token);
    }
  }, [regData, doConfirm, onRR]);

  React.useEffect(() => {
    if (_.get(confirmResponse, 'status') === 200) {
      if (doSubscription) {
        subscribe({ ...data, newUser: true });
      } else {
        onC && onC();
      }
    }
  }, [confirmResponse, data, doSubscription, onC, subscribe]);

  React.useEffect(() => {
    doSubscription && _.get(subResponse, 'status') === 204 && onC && onC();
  }, [subResponse, doSubscription, onC]);

  const start = () => {
    registrationDataRef.current = data;
    accountQuery(data.email);
  };

  return {
    isEmailUnique, isLoading, oauthProvider, start,
  };
}
